import React, { useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { normalizeOnboardingDetails } from "@/utils/hubMedApiNormalize";
import { DOCLINK_COLORS } from "@/config/colors";
import HubMedCommentComposer from "./HubMedCommentComposer";
import HubMedCommentsTimeline from "./HubMedCommentsTimeline";

const HubMedOnboardingDrawer = ({
  open,
  onClose,
  chemistId,
  details,
  detailsLoading,
  showAlert,
  onReplaceDetails,
  onAfterHide,
}) => {
  const [savingComment, setSavingComment] = useState(false);
  const [savingHidden, setSavingHidden] = useState(false);
  const [hideDialogOpen, setHideDialogOpen] = useState(false);

  const isHidden = Boolean(Number(details?.IsHidden ?? 0));

  const postComment = useCallback(
    async (comment) => {
      if (!chemistId) return;
      setSavingComment(true);
      try {
        const payload = await phappsRequest(
          API_PATHS.HUBMED_ADMIN_ONBOARDING_COMMENT,
          "POST",
          { chemistId, comment }
        );
        onReplaceDetails?.(normalizeOnboardingDetails(payload));
        showAlert?.({
          severity: "success",
          message: "Note added.",
        });
      } catch (err) {
        const msg = getAdminApiErrorMessage(err, "Could not save note");
        showAlert?.({ severity: "error", message: msg });
      } finally {
        setSavingComment(false);
      }
    },
    [chemistId, onReplaceDetails, showAlert]
  );

  const setHidden = useCallback(
    async (nextHidden) => {
      if (!chemistId) return;
      setSavingHidden(true);
      try {
        const payload = await phappsRequest(
          API_PATHS.HUBMED_ADMIN_ONBOARDING_SET_HIDDEN,
          "POST",
          { chemistId, isHidden: nextHidden }
        );
        onReplaceDetails?.(normalizeOnboardingDetails(payload));
        showAlert?.({
          severity: "success",
          message: nextHidden ? "Chemist hidden from onboarding list." : "Chemist unhidden.",
        });
        if (nextHidden) {
          onAfterHide?.();
          onClose?.();
        }
      } catch (err) {
        const msg = getAdminApiErrorMessage(err, "Could not update visibility");
        showAlert?.({ severity: "error", message: msg });
      } finally {
        setSavingHidden(false);
        setHideDialogOpen(false);
      }
    },
    [chemistId, onReplaceDetails, showAlert, onAfterHide, onClose]
  );

  const name = details?.ChemistName || "—";
  const phone = details?.Phone || "—";

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            overflow: "hidden",
            boxShadow: "-8px 0 24px rgba(64, 29, 94, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "100vw", sm: 440 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: "grey.50",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
              Onboarding
            </Typography>
            <IconButton aria-label="Close drawer" onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {detailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={36} sx={{ color: DOCLINK_COLORS.MID_PURPLE }} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    p: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
                    {name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {phone}
                  </Typography>
                  <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {!isHidden ? (
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={savingHidden}
                        onClick={() => setHideDialogOpen(true)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderWidth: 2,
                          "&:hover": { borderWidth: 2 },
                        }}
                      >
                        Hide chemist
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        disabled={savingHidden}
                        onClick={() => setHidden(false)}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Unhide chemist
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    borderRadius: 3,
                    border: "2px solid",
                    borderColor: DOCLINK_COLORS.LIGHT_PURPLE,
                    bgcolor: DOCLINK_COLORS.LAVENDER_TINT,
                    p: 2.5,
                    boxShadow: "0 2px 12px rgba(64, 29, 94, 0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ color: DOCLINK_COLORS.DARK_PURPLE, mb: 2 }}
                  >
                    Notes for {name}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "text.secondary" }}>
                    New note
                  </Typography>
                  <HubMedCommentComposer
                    disabled={!chemistId}
                    submitting={savingComment}
                    onSubmit={postComment}
                  />

                  <Divider sx={{ my: 2.5, borderColor: "rgba(64, 29, 94, 0.12)" }} />

                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: "text.secondary" }}>
                    Activity
                  </Typography>
                  <HubMedCommentsTimeline comments={details?.Comments} />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      <Dialog open={hideDialogOpen} onClose={() => !savingHidden && setHideDialogOpen(false)}>
        <DialogTitle>Hide chemist?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This chemist will disappear from the onboarding list. You can still unhide later from
            this panel if they reappear in another workflow.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHideDialogOpen(false)} disabled={savingHidden}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={savingHidden}
            onClick={() => setHidden(true)}
            sx={{ textTransform: "none" }}
          >
            {savingHidden ? "Saving…" : "Hide chemist"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HubMedOnboardingDrawer;
